import { z } from 'zod'
import { coupleFeedbackSentimentSchema } from '../enums/couple-feedback-sentiment.enum.js'
import { COUPLE_FEEDBACK_WORK_STATUSES } from '../enums/couple-feedback-status.enum.js'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'

/** One place a couple can review the venue in public. The button reads "Review on <name>". */
export const venueReviewLinkDtoSchema = z.object({
  name: z.string(),
  url: z.string(),
})

export type VenueReviewLinkDto = z.infer<typeof venueReviewLinkDtoSchema>

/**
 * A review place as the venue's staff see it in settings. `alsoOn` names the
 * company's other venues (those the reader can open) offering the same
 * address: usually a place copied from a sister venue by mistake.
 */
export const venueReviewLinkSettingDtoSchema = venueReviewLinkDtoSchema.extend({
  alsoOn: z.array(z.string()),
})

export type VenueReviewLinkSettingDto = z.infer<typeof venueReviewLinkSettingDtoSchema>

/** The venue's review places, in the order the couple sees them. May be empty. */
export const venueReviewLinksDtoSchema = z.object({
  links: z.array(venueReviewLinkSettingDtoSchema),
})

export type VenueReviewLinksDto = z.infer<typeof venueReviewLinksDtoSchema>

const HTTPS_LINK = /^https:\/\//i

/**
 * A review place the app can fill in for the venue, read from one of its
 * GoHighLevel sub-accounts: Google from the sub-account's Places ID, Facebook
 * from its page. `checkUrl` opens the listing itself, so staff can see whose it
 * is before adding it. `googlePlaceId` is set on a Google one.
 */
export const reviewLinkSuggestionDtoSchema = z.object({
  name: z.string(),
  url: z.string().regex(HTTPS_LINK),
  checkUrl: z.string().regex(HTTPS_LINK),
  from: z.string(),
  googlePlaceId: z.string().nullable(),
})

export type ReviewLinkSuggestionDto = z.infer<typeof reviewLinkSuggestionDtoSchema>

/**
 * What GoHighLevel knows about the venue's listings. Empty when the venue has
 * no sub-account connected, the company doesn't use GoHighLevel, or the
 * sub-accounts have no Google listing or Facebook page set.
 */
export const reviewLinkSuggestionsDtoSchema = z.object({
  suggestions: z.array(reviewLinkSuggestionDtoSchema),
})

export type ReviewLinkSuggestionsDto = z.infer<typeof reviewLinkSuggestionsDtoSchema>

/**
 * The places offered on the couple's page, without one whose address is not
 * https: each is a link they follow, so it is dropped rather than failing the page.
 */
export const offeredReviewLinksDtoSchema = z
  .array(venueReviewLinkDtoSchema)
  .transform((links) => links.filter((link) => HTTPS_LINK.test(link.url)))

/** What the couple already sent. */
export const coupleFeedbackAnswerDtoSchema = z.object({
  sentiment: coupleFeedbackSentimentSchema,
  body: z.string().nullable(),
  sentAt: instantSchema,
})

export type CoupleFeedbackAnswerDto = z.infer<typeof coupleFeedbackAnswerDtoSchema>

/**
 * The page one person in the couple sees. The review links are the venue's,
 * whatever they answered. Status, assignee and the venue's note are absent.
 */
export const coupleFeedbackPageDtoSchema = z.object({
  venueName: z.string(),
  eventName: z.string(),
  reviewLinks: offeredReviewLinksDtoSchema,
  answer: coupleFeedbackAnswerDtoSchema.nullable(),
})

export type CoupleFeedbackPageDto = z.infer<typeof coupleFeedbackPageDtoSchema>

/**
 * The first time this person opened one of the venue's review places from
 * their page. The name is the one the place had then. Opening it is not a
 * published review.
 */
export const coupleFeedbackReviewClickDtoSchema = z.object({
  name: z.string(),
  clickedAt: instantSchema,
})

export type CoupleFeedbackReviewClickDto = z.infer<typeof coupleFeedbackReviewClickDtoSchema>

/** Fields both kinds of answer carry for the venue's staff. */
const coupleFeedbackStaffBaseSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  eventName: z.string(),
  eventDate: calendarDaySchema.nullable(),
  guestCount: z.number().int(),
  venueId: z.string().uuid(),
  venueName: z.string(),
  authorName: z.string(),
  sentAt: instantSchema,
  internalNote: z.string().nullable(),
  reviewClicks: z.array(coupleFeedbackReviewClickDtoSchema),
})

/**
 * Something that needs attention: a note, and work the venue moves from new
 * to resolved. The database checks couple this to `needs_attention`.
 */
export const coupleFeedbackNeedsAttentionDtoSchema = coupleFeedbackStaffBaseSchema.extend({
  sentiment: z.literal('needs_attention'),
  body: z.string(),
  status: z.enum(COUPLE_FEEDBACK_WORK_STATUSES),
  assigneeId: z.string().uuid().nullable(),
  assigneeName: z.string().nullable(),
  resolvedAt: instantSchema.nullable(),
})

export type CoupleFeedbackNeedsAttentionDto = z.infer<typeof coupleFeedbackNeedsAttentionDtoSchema>

/** Praise. A record, not a task: no status, no assignee, nothing to resolve. */
export const coupleFeedbackWonderfulDtoSchema = coupleFeedbackStaffBaseSchema.extend({
  sentiment: z.literal('wonderful'),
  body: z.string().nullable(),
})

export type CoupleFeedbackWonderfulDto = z.infer<typeof coupleFeedbackWonderfulDtoSchema>

/** One answer, as the venue's staff see it. The sentiment decides the shape. */
export const coupleFeedbackDtoSchema = z.discriminatedUnion('sentiment', [
  coupleFeedbackNeedsAttentionDtoSchema,
  coupleFeedbackWonderfulDtoSchema,
])

export type CoupleFeedbackDto = z.infer<typeof coupleFeedbackDtoSchema>

/** Problems first, then praise. Each pile is only that kind of answer. */
export const coupleFeedbackListDtoSchema = z.object({
  needsAttention: z.array(coupleFeedbackNeedsAttentionDtoSchema),
  kindWords: z.array(coupleFeedbackWonderfulDtoSchema),
})

export type CoupleFeedbackListDto = z.infer<typeof coupleFeedbackListDtoSchema>

/** Every answer on one event, for the venue's staff. */
export const coupleFeedbackForEventDtoSchema = z.array(coupleFeedbackDtoSchema)

export type CoupleFeedbackForEventDto = z.infer<typeof coupleFeedbackForEventDtoSchema>
